/**
 * Vista previa de la obra sin marco en la ficha de arte.
 *
 * Con un acabado elegido, la galeria enseña la foto de la obra ya enmarcada y para volver
 * a ver la obra sola habia que seleccionar de nuevo la opcion sin enmarcar. Pasando el
 * raton por la imagen se cambia a la obra sin marco mientras dure el hover.
 *
 * La foto sin marco la publica el template de arte en el contenedor de variaciones: es la
 * imagen principal del producto, que es la que el importador asigna a la variacion sin
 * enmarcar. Deducirla del slug del atributo no valdria, porque cambia con el idioma.
 */

const CONTENEDOR_IMAGEN = '.wvg-single-gallery-image-container';

/* Mientras se enseña la obra sin marco, la lupa sobra: el zoom abriria la foto del
   acabado, que no es la que se esta viendo. La clase apaga el cursor de zoom por CSS. */
const CLASE_PREVIA = 'sc-art-frame-preview';

/**
 * @param {HTMLImageElement} img
 * @param {{src: string, srcset: string}} imagen
 */
function pintarImagen(img, imagen) {
    if (!imagen.src || img.getAttribute('src') === imagen.src) {
        return;
    }

    img.setAttribute('src', imagen.src);

    if (imagen.srcset) {
        img.setAttribute('srcset', imagen.srcset);
    } else {
        img.removeAttribute('srcset');
    }
}

/* Al cambiar de acabado WooCommerce no repinta la galeria por dentro: sustituye el nodo
   entero, y con el se va cualquier listener colgado de el. Se comprobo en stage que se
   reemplazan tanto .woo-variation-gallery-wrapper como la raiz .woocommerce-product-gallery,
   asi que la vista previa dejaba de responder en cuanto se elegia un marco, que es justo
   cuando sirve para algo. Se escucha en document, que no lo sustituye nadie, y se filtra por
   el destino de cada evento. */
export function initArtFrameHover() {
    const variaciones = document.querySelector('.variations[data-art-unframed-src]');
    const formulario = document.querySelector('form.variations_form');

    if (!variaciones || !formulario) {
        return;
    }

    const ficha = document.querySelector('.entry.product.is-art');

    const sinMarco = {
        src: variaciones.dataset.artUnframedSrc || '',
        srcset: variaciones.dataset.artUnframedSrcset || '',
    };

    /* Las variaciones del formulario son la fuente de verdad para deshacer la vista
       previa. WooCommerce las omite en los productos con muchas combinaciones y las pide
       por ajax; sin ellas no se puede saber que foto tocaba, asi que no se activa nada
       antes que arriesgarse a dejar la imagen equivocada. Se leen en cada consulta y no
       una sola vez, porque el atributo se reescribe cuando llegan por ajax. */
    const leerVariaciones = () => {
        try {
            return JSON.parse(formulario.dataset.product_variations || 'null') || [];
        } catch (error) {
            return [];
        }
    };

    if (!sinMarco.src || leerVariaciones().length === 0) {
        return;
    }

    /* No se guarda la imagen que habia antes del hover: el plugin de galeria repinta al
       cambiar de acabado y ese recuerdo se queda desfasado, que es como la imagen acababa
       congelada en la obra al cambiar de marco sin soltar el raton. Se recalcula siempre
       desde la variacion activa. */
    let hoverActivo = false;

    /* Se busca en cada consulta y no se guarda: el nodo cambia en cada cambio de acabado. */
    const imagenVisible = () => document.querySelector(`${CONTENEDOR_IMAGEN} img`);

    const imagenDeVariacionActiva = () => {
        const id = parseInt(formulario.querySelector('input[name="variation_id"]')?.value || '0', 10);
        const variacion = leerVariaciones().find((v) => v.variation_id === id);

        if (!variacion || !variacion.image || !variacion.image.src) {
            return sinMarco;
        }

        return { src: variacion.image.src, srcset: variacion.image.srcset || '' };
    };

    const mostrarSinMarco = () => {
        hoverActivo = true;

        const img = imagenVisible();

        if (!img) {
            return;
        }

        /* Con la opcion sin enmarcar ya se ve la obra: no hay previa que marcar y el zoom
           debe seguir disponible, que es justo para lo que sirve esa opcion. */
        const cambia = img.getAttribute('src') !== sinMarco.src;

        pintarImagen(img, sinMarco);

        if (cambia && ficha) {
            ficha.classList.add(CLASE_PREVIA);
        }
    };

    const restaurar = () => {
        hoverActivo = false;

        if (ficha) {
            ficha.classList.remove(CLASE_PREVIA);
        }

        const img = imagenVisible();

        if (img) {
            pintarImagen(img, imagenDeVariacionActiva());
        }
    };

    document.addEventListener('mouseover', (evento) => {
        if (evento.target.closest(CONTENEDOR_IMAGEN)) {
            mostrarSinMarco();
        }
    });

    document.addEventListener('mouseout', (evento) => {
        if (!evento.target.closest(CONTENEDOR_IMAGEN)) {
            return;
        }

        const sigueDentro = evento.relatedTarget && evento.relatedTarget.closest(CONTENEDOR_IMAGEN);

        if (!sigueDentro) {
            restaurar();
        }
    });

    /* El enlace de la galeria abre la foto del acabado en el visor, que durante la vista
       previa no es la que se esta mirando. Se corta en captura, antes de que el visor
       reciba el clic. Con la opcion sin enmarcar no hay previa y el zoom sigue entero. */
    document.addEventListener('click', (evento) => {
        if (!ficha || !ficha.classList.contains(CLASE_PREVIA)) {
            return;
        }

        if (evento.target.closest(CONTENEDOR_IMAGEN)) {
            evento.preventDefault();
            evento.stopPropagation();
        }
    }, true);

    /* Cambiar de acabado con el raton encima: el plugin acaba de pintar la foto del marco
       nuevo, pero el raton no se ha movido, asi que la vista previa debe seguir. Se espera
       un tick a que el plugin termine de repintar. */
    jQuery(document.body).on('found_variation reset_data', () => {
        if (!hoverActivo) {
            return;
        }

        window.setTimeout(mostrarSinMarco, 0);
    });
}
